import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'

interface Props {
  children: ReactNode
  /** Optional custom fallback. Defaults to a full-page error card. */
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  message: string
}

/**
 * Catches render errors in the component tree and renders a fallback UI
 * instead of crashing the whole application.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, message: '' }
  }

  static getDerivedStateFromError(error: unknown): State {
    const message = error instanceof Error ? error.message : String(error)
    return { hasError: true, message }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Replace with a real error reporting service (e.g. Sentry) when available.
    console.error('[ErrorBoundary]', error, info.componentStack)
  }

  handleReset = () => {
    // Full reload clears any stuck render/HMR state that setState alone cannot recover from.
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback

      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 p-8">
          <div className="border-pax-purple/20 w-full max-w-md rounded-2xl border bg-white p-8 text-center shadow-lg">
            <p className="text-pax-gray mb-2 text-5xl font-black opacity-20">500</p>
            <h1 className="text-pax-black mb-2 text-xl font-bold">Something went wrong</h1>
            <p className="text-pax-gray mb-6 text-sm">{this.state.message}</p>
            <button
              type="button"
              onClick={this.handleReset}
              className="bg-pax-blue hover:bg-pax-blue/90 rounded-xl px-5 py-2 text-sm font-medium text-white transition-colors focus-visible:outline-none"
            >
              Try again
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
